import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ProjectService } from 'src/app/Services/project.service';
import { SharedService } from '../Services/sharedService';
import { hasCompanyRole } from './role.utils';
import { ActivationJourneyService } from '../onboarding-checklist/activation-journey.service';
import { ActivationDestinationService } from '../onboarding-checklist/activation-destination.service';
import { ActivationEmployeeHandoffService } from '../onboarding-checklist/activation-employee-handoff.service';
import { OnboardingChecklistService } from '../onboarding-checklist/onboarding-checklist.service';
import { TokenEncryptionService } from '../Services/token-encryption.service';
import { sysConfig } from '../sysConfig';

export const onBoardingGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const projectService = inject(ProjectService);
  const sharedService = inject(SharedService);
  const activationJourney = inject(ActivationJourneyService);
  const activationDestination = inject(ActivationDestinationService);
  const employeeHandoff = inject(ActivationEmployeeHandoffService);
  const onboardingService = inject(OnboardingChecklistService);
  const tokenEncryptionService = inject(TokenEncryptionService);

  return projectService.getUser().pipe(
    switchMap((res: any) => {
      // Company signup wizard + checklist are for the `company` role only.
      if (!hasCompanyRole(res)) {
        return of(true);
      }

      // If the user has explicitly skipped the setup checklist, allow them to reach the dashboard.
      // This prevents a redirection loop if the profile is technically incomplete but they want to see data.
      const skippedOnboarding = localStorage.getItem('supersee_onboarding_checklist')
        ? JSON.parse(localStorage.getItem('supersee_onboarding_checklist')).skipped
        : false;
      const skippedTimestamp = localStorage.getItem('onboardingSkippedTimestamp');
      if (skippedOnboarding || skippedTimestamp) {
        const now = Date.now();
        const elapsed = skippedTimestamp ? now - parseInt(skippedTimestamp, 10) : 0;
        // If explicitly skipped via the new checklist system or within 30 seconds of a legacy skip
        if (skippedOnboarding || elapsed < 30000) {
          return of(true);
        }
      }

      // Mandatory: company profile + trial signup fields (incl. phone OTP path via meta).
      // checkingOnBoarding === false means profile is incomplete — send to company onboarding wizard only.
      const profileComplete = sharedService.checkingOnBoarding(res);
      if (!profileComplete) {
        if (state.url?.startsWith('/setup')) {
          return of(true);
        }
        void router.navigate(['/setup/ready'], { replaceUrl: true });
        return of(false);
      }

      // Activation journey (flag on): nudge incomplete journeys to the resolver destination.
      // Paused / private-eval / completed / skipped do not force a redirect (see shouldRedirectToSetup).
      // Active employee handoff may visit /employees for the canonical New Employee modal.
      const onSetupRoute = state.url?.startsWith('/setup');
      const pathOnly = (state.url || '').split('?')[0];
      const onEmployeesHandoff = pathOnly === '/employees' && employeeHandoff.isActive();
      if (
        !onSetupRoute &&
        !onEmployeesHandoff &&
        sysConfig.activationJourneyEnabled &&
        !onboardingService.isPermanentlyCompleted() &&
        activationJourney.shouldRedirectToSetup()
      ) {
        const dest = activationDestination.resolve({
          state: activationJourney.getState(),
          requestedPath: state.url,
        });
        void router.navigate([dest.route], {
          queryParams: dest.queryParams || {},
          replaceUrl: true,
        });
        return of(false);
      }

      // Trial users who haven't started the activation journey yet should still
      // be directed to /setup on navigation (fresh journey state only — not paused/skipped).
      if (
        !onSetupRoute &&
        sysConfig.activationJourneyEnabled &&
        !onboardingService.isPermanentlyCompleted() &&
        !activationJourney.shouldRedirectToSetup()
      ) {
        const js = activationJourney.getState();
        const isFresh =
          js.completedSteps.length === 0 &&
          !js.goal &&
          !js.modeEducationSeen &&
          !js.privateTestEmployeeId &&
          !js.firstEmployeeId &&
          !js.continuedLater &&
          js.journeyStatus !== 'paused' &&
          js.journeyStatus !== 'skipped' &&
          js.journeyStatus !== 'completed' &&
          !js.finished &&
          !js.skipped;

        if (isFresh) {
          const user =
            tokenEncryptionService.getEncryptedItem<any>('user') ||
            tokenEncryptionService.getEncryptedItem<any>('logedInUser');
          const quotaStatus = user?.meta?.quota_status;
          const isTrial = String(quotaStatus || '').toLowerCase() === 'trial';
          if (isTrial) {
            return activationDestination.postLoginDestination().pipe(
              map((dest) => {
                if (dest.route.startsWith('/setup')) {
                  void router.navigate([dest.route], {
                    queryParams: dest.queryParams || {},
                    replaceUrl: true,
                  });
                  return false;
                }
                return true;
              })
            );
          }
        }
      }

      // Classic setup checklist (/setup) stays optional once profile is verified.
      return of(true);
    })
  );
};
